import { useCallback } from 'react'
import { MessageSquare } from 'lucide-react'
import { StoryboardsSection } from '../../../components/Report/ReportComponents'
import { SurveyInsightsSection } from '../../../components/Report/SurveyInsightsSection'
import type { StoryboardData } from '../../../components/Report/ReportComponents'
import type {
  MindMapRecord,
  MindMapNode,
  StakeholderRating,
  StakeholderRatings,
  MindMapNodeData,
} from '@bandos/shared'
import type { SectionKey } from '../../../features/reportSections'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import {
  StakeholderFeedbackBox,
  FeedbackHeader,
  FeedbackHeaderText,
  StakeholderCard,
  StakeholderHeader,
  StakeholderName,
  StakeholderRole,
  StakeholderRatingText,
  FeedbackFieldsContainer,
  FeedbackField,
  FeedbackFieldLabel,
  FeedbackFieldContent,
} from '../../styles/ReportPageStyles'

export function useIdeaExtras(
  mindmap: MindMapRecord | null,
  nodeMap: Map<string, MindMapNode>,
  getEffectiveNodeData: (
    node: MindMapNode
  ) => MindMapNodeData & { stakeholderRating?: StakeholderRatings },
  showStakeholderRatings: boolean,
  isVisible: (key: SectionKey) => boolean
) {
  const { isFeatureEnabled } = useFeatureAccessContext()
  const stakeholdersFeatureEnabled = isFeatureEnabled('simulated_stakeholders')

  // Render stakeholder feedback for an idea (extra content for shared IdeaItem)
  const renderStakeholderFeedback = useCallback(
    (nodeId: string) => {
      if (!stakeholdersFeatureEnabled || !showStakeholderRatings) return null

      const node = nodeMap.get(nodeId)
      if (!node) return null

      const ideaData = getEffectiveNodeData(node)
      if (!ideaData.stakeholderRating) return null

      return (
        <StakeholderFeedbackBox>
          <FeedbackHeader>
            <MessageSquare size={14} color="#000000" />
            <FeedbackHeaderText>Stakeholder Feedback</FeedbackHeaderText>
          </FeedbackHeader>

          {ideaData.stakeholderRating.stakeholders.length > 0 && (
            <>
              {ideaData.stakeholderRating.stakeholders.map(
                (stakeholder: StakeholderRating, idx: number) => (
                  <StakeholderCard key={idx}>
                    <StakeholderHeader>
                      <div>
                        <StakeholderName>{stakeholder.name}</StakeholderName>
                        <StakeholderRole> - {stakeholder.role}</StakeholderRole>
                      </div>
                      <StakeholderRatingText>{stakeholder.rating}/10</StakeholderRatingText>
                    </StakeholderHeader>
                    <FeedbackFieldsContainer>
                      <FeedbackField>
                        <FeedbackFieldLabel>Main risks</FeedbackFieldLabel>
                        <FeedbackFieldContent>{stakeholder.mainRisks}</FeedbackFieldContent>
                      </FeedbackField>
                      <FeedbackField>
                        <FeedbackFieldLabel>Suggestions</FeedbackFieldLabel>
                        <FeedbackFieldContent>{stakeholder.suggestions}</FeedbackFieldContent>
                      </FeedbackField>
                      <FeedbackField>
                        <FeedbackFieldLabel>Question</FeedbackFieldLabel>
                        <FeedbackFieldContent>{stakeholder.question}</FeedbackFieldContent>
                      </FeedbackField>
                    </FeedbackFieldsContainer>
                  </StakeholderCard>
                )
              )}
            </>
          )}
        </StakeholderFeedbackBox>
      )
    },
    [stakeholdersFeatureEnabled, showStakeholderRatings, nodeMap, getEffectiveNodeData]
  )

  // Get storyboard children for an idea node
  const getChildStoryboards = useCallback(
    (ideaId: string): StoryboardData[] => {
      if (!mindmap) return []
      return mindmap.content.edges
        .filter((edge) => edge.source === ideaId)
        .map((edge) => nodeMap.get(edge.target))
        .filter((node): node is MindMapNode => node !== undefined && node.type === 'storyboardNode')
        .map((node) => {
          const data = node.data as MindMapNodeData & {
            storyboardTitle?: string
            storyboardUrls?: string[]
            frameCaptions?: string[]
            isGenerating?: boolean
          }
          return {
            id: node.id,
            storyboardTitle: data.storyboardTitle,
            storyboardUrls: data.storyboardUrls ?? [],
            frameCaptions: data.frameCaptions,
            isGenerating: data.isGenerating,
          }
        })
    },
    [mindmap, nodeMap]
  )

  // Get survey children for any node
  const getChildSurveys = useCallback(
    (ideaId: string): string[] => {
      if (!mindmap) return []
      return mindmap.content.edges
        .filter((edge) => edge.source === ideaId)
        .map((edge) => nodeMap.get(edge.target))
        .filter((node): node is MindMapNode => node !== undefined && node.type === 'surveyNode')
        .map((node) => node.id)
    },
    [mindmap, nodeMap]
  )

  // Render surveys attached to a storyboard node
  const renderStoryboardNodeExtra = useCallback(
    (storyboardNodeId: string) => {
      if (!isVisible('survey')) return null
      const surveyIds = getChildSurveys(storyboardNodeId)
      if (surveyIds.length === 0) return null
      return (
        <>
          {surveyIds.map((id) => (
            <SurveyInsightsSection key={id} nodeId={id} />
          ))}
        </>
      )
    },
    [getChildSurveys, isVisible]
  )

  // Render storyboards for any node, including surveys attached to each storyboard
  const renderIdeaStoryboards = useCallback(
    (nodeId: string) => {
      if (!isVisible('storyboard')) return null
      const storyboards = getChildStoryboards(nodeId)
      if (storyboards.length === 0) return null
      return (
        <StoryboardsSection storyboards={storyboards} renderExtra={renderStoryboardNodeExtra} />
      )
    },
    [getChildStoryboards, isVisible, renderStoryboardNodeExtra]
  )

  // Render survey insights for any node
  const renderIdeaSurveys = useCallback(
    (ideaId: string) => {
      if (!isVisible('survey')) return null
      const surveyIds = getChildSurveys(ideaId)
      if (surveyIds.length === 0) return null
      return (
        <>
          {surveyIds.map((id) => (
            <SurveyInsightsSection key={id} nodeId={id} />
          ))}
        </>
      )
    },
    [getChildSurveys, isVisible]
  )

  // Combined renderer: stakeholder feedback + storyboards + surveys
  const renderIdeaExtras = useCallback(
    (nodeId: string) => {
      const feedback = renderStakeholderFeedback(nodeId)
      const storyboards = renderIdeaStoryboards(nodeId)
      const surveys = renderIdeaSurveys(nodeId)
      if (!feedback && !storyboards && !surveys) return null
      return (
        <>
          {feedback}
          {storyboards}
          {surveys}
        </>
      )
    },
    [renderStakeholderFeedback, renderIdeaStoryboards, renderIdeaSurveys]
  )

  return { renderIdeaExtras }
}
